import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Brush,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { AnalysisDetail, Row } from "@/lib/api";
import { percent, price, signedPercent } from "@/lib/format";

// Overlay keys correspond one-to-one with the tiles in ScoreBreakdown.
// Exported so TickerDetail can type its state without circular imports.
export type SignalKey = "hit" | "atr" | "trend" | "rs" | "ret" | "pos" | "rr";

interface ChartPanelProps {
  detail: AnalysisDetail;
  row?: Row;
  activeSignal?: SignalKey | null;
}

// One data point per bar. Markers for peaks/troughs and the current
// price are emitted as parallel keys that Recharts' Scatter series can
// pick up — undefined values are ignored by the renderer. Overlay-only
// keys (atrUpper/atrLower/sma50) are populated on demand so the default
// view stays identical to the previous behavior.
interface Point {
  date: string;
  close: number;
  peakIn?: number;
  peakOut?: number;
  troughIn?: number;
  troughOut?: number;
  now?: number;
  atrUpper?: number;
  atrLower?: number;
  sma50?: number;
}

// Last-N window constants for overlays that emphasize the most recent
// bars (ret % target corridor, R:R reward/risk zones). Kept small so
// the rest of the chart stays legible.
const RET_WINDOW_BARS = 20;
const RR_WINDOW_BARS = 30;
const SMA_PERIOD = 50;
const TREND_SLOPE_WINDOW = 40;
const HIT_RATE_THRESHOLD = 0.8;
const RS_WINDOW = 60;

export function ChartPanel({ detail, row, activeSignal = null }: ChartPanelProps) {
  const [bandLo, bandHi] = detail.regime_band;

  const peakSet = useMemo(() => new Set(detail.peak_indices), [detail.peak_indices]);
  const troughSet = useMemo(
    () => new Set(detail.trough_indices),
    [detail.trough_indices]
  );

  // Rolling mean of `close` over SMA_PERIOD bars; NaN for positions with
  // insufficient history. Mirrors internal/analysis/indicators.go so the
  // client-drawn SMA(50) line matches the backend's trend classifier.
  const sma50Series = useMemo(
    () => rollingMean(detail.close, SMA_PERIOD),
    [detail.close]
  );

  const data: Point[] = useMemo(() => {
    const n = detail.dates.length;
    return detail.dates.map((date, i) => {
      const close = detail.close[i] ?? Number.NaN;
      const inRegime = close >= bandLo - 1e-9 && close <= bandHi + 1e-9;
      const point: Point = { date, close };
      if (peakSet.has(i)) {
        if (inRegime) point.peakIn = close;
        else point.peakOut = close;
      }
      if (troughSet.has(i)) {
        if (inRegime) point.troughIn = close;
        else point.troughOut = close;
      }
      if (i === n - 1) {
        point.now = close;
      }
      // ATR envelope (populated only when the atr overlay is active).
      if (activeSignal === "atr" && detail.atr_series) {
        const atrAt = detail.atr_series[i];
        if (atrAt != null && Number.isFinite(atrAt)) {
          point.atrUpper = close + atrAt;
          point.atrLower = close - atrAt;
        }
      }
      // SMA(50) line (populated only when the trend overlay is active).
      if (activeSignal === "trend") {
        const sma = sma50Series[i];
        if (sma != null && Number.isFinite(sma)) point.sma50 = sma;
      }
      return point;
    });
  }, [
    detail.dates,
    detail.close,
    detail.atr_series,
    peakSet,
    troughSet,
    bandLo,
    bandHi,
    activeSignal,
    sma50Series,
  ]);

  const n = detail.dates.length;
  // Brush window = last 120 bars (~6 months daily) by default, but the
  // user can narrow it further by dragging across the chart or by
  // dragging the Brush handles directly. State is kept in sync with
  // both input methods so they stay interchangeable.
  const defaultBrushWindow = useCallback(
    (len: number): [number, number] => [Math.max(0, len - 120), Math.max(0, len - 1)],
    []
  );
  const [brushWindow, setBrushWindow] = useState<[number, number]>(() =>
    defaultBrushWindow(n)
  );
  // Drag-to-zoom state: start/end indices into the data array. Null
  // while not actively dragging.
  const [drag, setDrag] = useState<{ start: number; end: number } | null>(null);
  // Reset zoom + drag whenever the underlying data (ticker) changes.
  useEffect(() => {
    setBrushWindow(defaultBrushWindow(detail.dates.length));
    setDrag(null);
  }, [detail.dates, defaultBrushWindow]);

  const onChartMouseDown = useCallback((e: unknown) => {
    const idx = extractTooltipIndex(e);
    if (idx != null) setDrag({ start: idx, end: idx });
  }, []);

  const onChartMouseMove = useCallback(
    (e: unknown) => {
      if (drag == null) return;
      const idx = extractTooltipIndex(e);
      if (idx != null) {
        setDrag((prev) => (prev ? { start: prev.start, end: idx } : null));
      }
    },
    [drag]
  );

  // Mirror drag state to a ref so the window-level mouseup listener below
  // always sees the latest value without re-registering every render.
  const dragRef = useRef<{ start: number; end: number } | null>(null);
  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  // Finalize on a window-level mouseup so releasing the mouse outside the
  // chart bounds still commits the selection. The previous version
  // cleared the drag on the chart's own mouseLeave event, which meant
  // you could not select all the way to the right: the cursor crossed
  // the plot-area boundary before the release and the selection vanished.
  useEffect(() => {
    const onWindowUp = () => {
      const d = dragRef.current;
      if (d == null) return;
      const lo = Math.min(d.start, d.end);
      const hi = Math.max(d.start, d.end);
      // Require a 3-bar drag so accidental clicks don't collapse the view.
      if (hi - lo >= 3) {
        setBrushWindow([lo, hi]);
      }
      setDrag(null);
    };
    window.addEventListener("mouseup", onWindowUp);
    return () => window.removeEventListener("mouseup", onWindowUp);
  }, []);

  const onBrushChange = useCallback(
    (range: { startIndex?: number; endIndex?: number }) => {
      const { startIndex, endIndex } = range;
      if (startIndex != null && endIndex != null && endIndex > startIndex) {
        setBrushWindow([startIndex, endIndex]);
      }
    },
    []
  );

  const onResetZoom = useCallback(() => {
    setBrushWindow(defaultBrushWindow(detail.dates.length));
    setDrag(null);
  }, [defaultBrushWindow, detail.dates.length]);

  const [brushStartIndex, brushEndIndex] = brushWindow;
  const zoomed =
    brushStartIndex !== Math.max(0, n - 120) || brushEndIndex !== Math.max(0, n - 1);

  return (
    <section className="rounded-lg border p-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-semibold">
          {detail.ticker} — close with support / resistance / peaks
        </h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span>drag on chart to zoom · double-click to reset</span>
          {zoomed && (
            <button
              type="button"
              onClick={onResetZoom}
              className="rounded border px-2 py-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              reset zoom
            </button>
          )}
        </div>
      </div>
      <div
        style={{ width: "100%", height: 320, cursor: "crosshair" }}
        onDoubleClick={onResetZoom}
      >
        <ResponsiveContainer>
          <ComposedChart
            data={data}
            margin={{ top: 5, right: 20, left: 5, bottom: 5 }}
            onMouseDown={onChartMouseDown}
            onMouseMove={onChartMouseMove}
          >
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              minTickGap={32}
              stroke="currentColor"
            />
            <YAxis
              domain={["auto", "auto"]}
              tick={{ fontSize: 10 }}
              stroke="currentColor"
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                color: "var(--popover-foreground)",
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {/* Default regime band shading (stays in place regardless of overlay). */}
            <ReferenceArea
              y1={detail.support}
              y2={detail.resistance}
              fill="#2ca02c"
              fillOpacity={0.06}
              stroke="none"
            />
            <ReferenceLine
              y={detail.support}
              stroke="#2ca02c"
              strokeDasharray="4 4"
              label={{
                value: `support ${detail.support.toFixed(2)}`,
                position: "insideBottomLeft",
                fontSize: 10,
              }}
            />
            <ReferenceLine
              y={detail.resistance}
              stroke="#d62728"
              strokeDasharray="4 4"
              strokeWidth={activeSignal === "ret" ? 2.5 : 1}
              label={{
                value: `resist ${detail.resistance.toFixed(2)}`,
                position: "insideTopLeft",
                fontSize: 10,
              }}
            />
            {renderPosOverlay(detail, row, activeSignal === "pos")}
            {renderRetOverlay(detail, row, data, activeSignal === "ret")}
            {renderRrOverlay(detail, row, data, activeSignal === "rr")}
            {renderHitOverlay(detail, activeSignal === "hit")}
            {renderTrendOverlay(detail, activeSignal === "trend")}
            <Line
              type="monotone"
              dataKey="close"
              stroke="#60a5fa"
              strokeWidth={1.5}
              dot={false}
              name="close"
              isAnimationActive={false}
            />
            {activeSignal === "atr" && (
              <>
                <Line
                  type="monotone"
                  dataKey="atrUpper"
                  stroke="#8b5cf6"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  dot={false}
                  name="close + 1 ATR"
                  isAnimationActive={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="atrLower"
                  stroke="#8b5cf6"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  dot={false}
                  name="close − 1 ATR"
                  isAnimationActive={false}
                  connectNulls
                />
              </>
            )}
            {activeSignal === "trend" && (
              <Line
                type="monotone"
                dataKey="sma50"
                stroke={trendStrokeColor(detail.trend)}
                strokeWidth={2}
                dot={false}
                name={`SMA(${SMA_PERIOD})`}
                isAnimationActive={false}
                connectNulls
              />
            )}
            <Scatter
              dataKey="peakIn"
              fill="#d62728"
              shape="triangle"
              name="peak (in regime)"
            />
            <Scatter
              dataKey="peakOut"
              fill="#d62728"
              fillOpacity={0.3}
              shape="triangle"
              name="peak (out)"
            />
            <Scatter
              dataKey="troughIn"
              fill="#2ca02c"
              shape="circle"
              name="trough (in regime)"
            />
            <Scatter
              dataKey="troughOut"
              fill="#2ca02c"
              fillOpacity={0.3}
              shape="circle"
              name="trough (out)"
            />
            <Scatter dataKey="now" fill="#111827" shape="diamond" name="current price" />
            {drag && drag.start !== drag.end && (
              <ReferenceArea
                x1={detail.dates[Math.min(drag.start, drag.end)]}
                x2={detail.dates[Math.max(drag.start, drag.end)]}
                stroke="currentColor"
                strokeOpacity={0.45}
                fill="currentColor"
                fillOpacity={0.08}
                ifOverflow="visible"
              />
            )}
            <Brush
              dataKey="date"
              height={24}
              // Default Brush fill is "#fff" and the default traveller
              // stroke is low-contrast, which makes the selected window
              // invisible on a white background. Use currentColor for
              // the outline and the muted token for the background so
              // both light and dark themes stay legible.
              stroke="currentColor"
              strokeOpacity={0.55}
              fill="var(--muted)"
              travellerWidth={10}
              startIndex={brushStartIndex}
              endIndex={brushEndIndex}
              onChange={onBrushChange}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {activeSignal && (
        <div className="text-xs text-muted-foreground mt-3">
          {overlayCaption(detail, row, activeSignal)}
        </div>
      )}
      {activeSignal === "rs" && <RsOverlayChart detail={detail} brushWindow={brushWindow} />}
    </section>
  );
}

// --- overlay renderers ---------------------------------------------------

function renderHitOverlay(detail: AnalysisDetail, active: boolean) {
  if (!active) return null;
  const [bandLo, bandHi] = detail.regime_band;
  const target = detail.support + HIT_RATE_THRESHOLD * (detail.resistance - detail.support);
  // Each swing leg is drawn as a standalone ReferenceLine.segment so
  // gaps between legs render cleanly (Line with nulls would still span
  // the x-gap between consecutive non-null points).
  const legs = detail.swings.flatMap((swing, idx) => {
    const startDate = detail.dates[swing.start_idx];
    const endDate = detail.dates[swing.end_idx];
    if (!startDate || !endDate) return [];
    const inRegime =
      swing.start_price >= bandLo - 1e-9 &&
      swing.start_price <= bandHi + 1e-9 &&
      swing.end_price >= bandLo - 1e-9 &&
      swing.end_price <= bandHi + 1e-9;
    const rangeWidth = detail.resistance - detail.support;
    const traversalOk =
      rangeWidth > 1e-9 &&
      swing.end_price - swing.start_price >= HIT_RATE_THRESHOLD * rangeWidth;

    let stroke: string;
    let strokeWidth: number;
    let dash: string | undefined;
    if (swing.direction !== "up") {
      return []; // down-swings don't contribute to hit rate
    }
    if (inRegime && traversalOk) {
      stroke = "#059669"; // emerald — qualifying + hit
      strokeWidth = 3;
    } else if (inRegime) {
      stroke = "#f59e0b"; // amber — qualifying + miss
      strokeWidth = 2;
    } else {
      stroke = "#9ca3af"; // gray — up-leg out of regime
      strokeWidth = 1;
      dash = "4 4";
    }
    return [
      <ReferenceLine
        key={`hit-leg-${idx}`}
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeDasharray={dash}
        ifOverflow="visible"
        segment={[
          { x: startDate, y: swing.start_price },
          { x: endDate, y: swing.end_price },
        ]}
      />,
    ];
  });
  return (
    <>
      {legs}
      <ReferenceLine
        y={target}
        stroke="#059669"
        strokeDasharray="2 4"
        strokeWidth={1}
        label={{
          value: `hit target ${target.toFixed(2)}`,
          position: "insideTopRight",
          fontSize: 10,
        }}
      />
    </>
  );
}

function renderPosOverlay(detail: AnalysisDetail, row: Row | undefined, active: boolean) {
  if (!active) return null;
  const { support, resistance, current_price } = detail;
  const range = resistance - support;
  if (!(range > 1e-9)) return null;
  const midLow = support + 0.25 * range;
  const midHigh = support + 0.5 * range;
  return (
    <>
      <ReferenceArea y1={support} y2={midLow} fill="#10b981" fillOpacity={0.12} stroke="none" />
      <ReferenceArea y1={midLow} y2={midHigh} fill="#f59e0b" fillOpacity={0.12} stroke="none" />
      <ReferenceArea y1={midHigh} y2={resistance} fill="#ef4444" fillOpacity={0.1} stroke="none" />
      <ReferenceLine
        y={current_price}
        stroke="#0ea5e9"
        strokeWidth={2}
        label={{
          value: `pos ${(detail.current_position * 100).toFixed(0)}%`,
          position: "insideTopRight",
          fontSize: 10,
        }}
      />
      {row && row.trigger > 0 && (
        <ReferenceLine
          y={row.trigger}
          stroke="#10b981"
          strokeDasharray="2 3"
          strokeWidth={1}
          label={{
            value: `trigger ${row.trigger.toFixed(2)}`,
            position: "insideBottomRight",
            fontSize: 10,
          }}
        />
      )}
    </>
  );
}

function renderRetOverlay(
  detail: AnalysisDetail,
  row: Row | undefined,
  data: Point[],
  active: boolean
) {
  if (!active) return null;
  const n = data.length;
  const start = data[Math.max(0, n - RET_WINDOW_BARS)]?.date;
  const end = data[n - 1]?.date;
  if (!start || !end) return null;
  return (
    <ReferenceArea
      x1={start}
      x2={end}
      y1={detail.current_price}
      y2={detail.resistance}
      fill="#10b981"
      fillOpacity={0.18}
      stroke="#10b981"
      strokeOpacity={0.4}
      strokeDasharray="2 4"
      label={{
        value: row
          ? `+${(row.expected_return_pct * 100).toFixed(1)}% target`
          : "target",
        position: "insideTopRight",
        fontSize: 10,
      }}
    />
  );
}

function renderRrOverlay(
  detail: AnalysisDetail,
  row: Row | undefined,
  data: Point[],
  active: boolean
) {
  if (!active || !row) return null;
  const n = data.length;
  const start = data[Math.max(0, n - RR_WINDOW_BARS)]?.date;
  const end = data[n - 1]?.date;
  if (!start || !end) return null;
  return (
    <>
      <ReferenceArea
        x1={start}
        x2={end}
        y1={detail.current_price}
        y2={detail.resistance}
        fill="#10b981"
        fillOpacity={0.15}
        stroke="none"
        label={{ value: "reward", position: "insideTopRight", fontSize: 10 }}
      />
      <ReferenceArea
        x1={start}
        x2={end}
        y1={row.stop_loss}
        y2={detail.current_price}
        fill="#ef4444"
        fillOpacity={0.15}
        stroke="none"
        label={{ value: "risk", position: "insideBottomRight", fontSize: 10 }}
      />
      <ReferenceLine
        y={row.stop_loss}
        stroke="#ef4444"
        strokeWidth={1.5}
        label={{
          value: `stop ${row.stop_loss.toFixed(2)}`,
          position: "insideBottomLeft",
          fontSize: 10,
        }}
      />
      {row.trail_distance > 0 && (
        <ReferenceLine
          y={detail.current_price - row.trail_distance}
          stroke="#f97316"
          strokeDasharray="2 3"
          strokeWidth={1}
          label={{
            value: `trail ${(detail.current_price - row.trail_distance).toFixed(2)}`,
            position: "insideBottomLeft",
            fontSize: 10,
          }}
        />
      )}
    </>
  );
}

function renderTrendOverlay(detail: AnalysisDetail, active: boolean) {
  if (!active) return null;
  const n = detail.dates.length;
  const windowStart = n - 1 - TREND_SLOPE_WINDOW;
  if (windowStart < 0) return null;
  return (
    <ReferenceArea
      x1={detail.dates[windowStart]}
      x2={detail.dates[n - 1]}
      fill="#6366f1"
      fillOpacity={0.08}
      stroke="#6366f1"
      strokeOpacity={0.3}
      strokeDasharray="2 4"
      label={{
        value: `${TREND_SLOPE_WINDOW}-bar slope window`,
        position: "insideTop",
        fontSize: 10,
      }}
    />
  );
}

// --- RS comparison chart (separate panel) --------------------------------

interface RsPoint {
  date: string;
  stockNorm?: number;
  benchNorm?: number;
}

function RsOverlayChart({
  detail,
  brushWindow,
}: {
  detail: AnalysisDetail;
  brushWindow: [number, number];
}) {
  const data: RsPoint[] = useMemo(() => {
    const n = detail.dates.length;
    const benchSeries = detail.benchmark_close;
    if (!benchSeries || n <= RS_WINDOW) return [];
    const windowStart = n - 1 - RS_WINDOW;
    const stockBase = detail.close[windowStart];
    const benchBase = benchSeries[windowStart];
    if (
      stockBase == null ||
      !(stockBase > 0) ||
      benchBase == null ||
      !Number.isFinite(benchBase)
    ) {
      return [];
    }
    return detail.dates.map((date, i) => {
      const point: RsPoint = { date };
      if (i >= windowStart) {
        const stockAt = detail.close[i];
        if (stockAt != null) {
          point.stockNorm = stockAt / stockBase - 1;
        }
        const b = benchSeries[i];
        if (b != null && Number.isFinite(b)) {
          point.benchNorm = b / benchBase - 1;
        }
      }
      return point;
    });
  }, [detail.dates, detail.close, detail.benchmark_close]);

  // Clip the series to the main chart's current zoom so both charts
  // share the same X range. The RS series is only populated over the
  // last RS_WINDOW bars, so an early brush window may have no data at
  // all — that case is handled below with a dedicated empty state.
  const [brushStart, brushEnd] = brushWindow;
  const visible = useMemo(() => {
    if (data.length === 0) return data;
    const from = Math.max(0, brushStart);
    const to = Math.min(detail.dates.length - 1, brushEnd);
    if (from > to) return [];
    return data.slice(from, to + 1);
  }, [data, brushStart, brushEnd, detail.dates.length]);

  if (data.length === 0) {
    return (
      <div className="mt-3 rounded border p-3 text-xs text-muted-foreground">
        Relative strength overlay needs a resolved benchmark and at least{" "}
        {RS_WINDOW + 1} bars of history.
      </div>
    );
  }

  if (!visible.some((p) => p.stockNorm != null)) {
    return (
      <div className="mt-3 rounded border p-3 text-xs text-muted-foreground">
        Zoom the main chart to include the last {RS_WINDOW} bars to see the relative strength overlay.
      </div>
    );
  }

  const n = detail.dates.length;
  const windowStart = n - 1 - RS_WINDOW;
  const stockBase = detail.close[windowStart];
  const stockLast = detail.close[n - 1];
  const benchSeries = detail.benchmark_close;
  const benchBase = benchSeries?.[windowStart];
  const benchLast = benchSeries?.[n - 1];
  const stockReturn =
    stockBase != null && stockBase > 0 && stockLast != null
      ? stockLast / stockBase - 1
      : null;
  const benchReturn =
    benchBase != null &&
    Number.isFinite(benchBase) &&
    benchBase > 0 &&
    benchLast != null &&
    Number.isFinite(benchLast)
      ? benchLast / benchBase - 1
      : null;
  const rsDelta =
    stockReturn != null && benchReturn != null ? stockReturn - benchReturn : null;

  return (
    <div className="mt-3 rounded border p-3">
      <div className="text-xs font-semibold mb-1">
        Relative strength — normalized over last {RS_WINDOW} bars vs {detail.benchmark || "benchmark"}
      </div>
      <div className="text-xs text-muted-foreground mb-2">
        {detail.ticker} {signedPercent(stockReturn, 1)} · {detail.benchmark || "bench"}{" "}
        {signedPercent(benchReturn, 1)} = {signedPercent(rsDelta, 1)} relative
      </div>
      <div style={{ width: "100%", height: 160 }}>
        <ResponsiveContainer>
          <ComposedChart data={visible} margin={{ top: 5, right: 20, left: 5, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10 }}
              minTickGap={32}
              stroke="currentColor"
            />
            <YAxis
              tick={{ fontSize: 10 }}
              stroke="currentColor"
              tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
            />
            <Tooltip
              contentStyle={{
                background: "var(--popover)",
                border: "1px solid var(--border)",
                color: "var(--popover-foreground)",
                fontSize: 12,
              }}
              formatter={(value) => {
                if (typeof value !== "number") return String(value);
                return `${(value * 100).toFixed(2)}%`;
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="currentColor" strokeOpacity={0.4} />
            <Line
              type="monotone"
              dataKey="stockNorm"
              stroke="#60a5fa"
              strokeWidth={2}
              dot={false}
              name={detail.ticker}
              isAnimationActive={false}
              connectNulls
            />
            <Line
              type="monotone"
              dataKey="benchNorm"
              stroke="#f97316"
              strokeWidth={1.5}
              strokeDasharray="5 3"
              dot={false}
              name={detail.benchmark || "bench"}
              isAnimationActive={false}
              connectNulls
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// --- helpers -------------------------------------------------------------

// extractTooltipIndex pulls the active bar index out of the Recharts
// mouse-event state object. In Recharts v3 the selector coerces the
// index to a string (see combineActiveTooltipIndex) so we parse it;
// v2 used a raw number, so we accept both shapes for safety.
function extractTooltipIndex(state: unknown): number | null {
  if (!state || typeof state !== "object") return null;
  const raw = (state as { activeTooltipIndex?: unknown }).activeTooltipIndex;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function rollingMean(values: number[], period: number): number[] {
  const out = new Array<number>(values.length).fill(Number.NaN);
  if (values.length < period) return out;
  let sum = 0;
  for (let i = 0; i < period; i++) sum += values[i] ?? 0;
  out[period - 1] = sum / period;
  for (let i = period; i < values.length; i++) {
    sum += (values[i] ?? 0) - (values[i - period] ?? 0);
    out[i] = sum / period;
  }
  return out;
}

function trendStrokeColor(trend: AnalysisDetail["trend"]): string {
  switch (trend) {
    case "up":
      return "#10b981";
    case "down":
      return "#ef4444";
    default:
      return "#64748b";
  }
}

function overlayCaption(
  detail: AnalysisDetail,
  row: Row | undefined,
  key: SignalKey
): string {
  switch (key) {
    case "hit": {
      if (detail.hit_rate == null) {
        return "hit — no qualifying in-regime up-swings; overlay shows attempts only.";
      }
      return `hit ${percent(detail.hit_rate)} — emerald legs cleared 80% of the support/resistance range; amber legs fell short.`;
    }
    case "atr":
      return `atr ${percent(detail.atr_pct, 1)} — dashed band = close ± 1 ATR ($${price(detail.atr)}).`;
    case "trend":
      if (detail.trend === "unknown") {
        return "trend — insufficient bars to compute SMA(50) slope.";
      }
      return `trend ${detail.trend} — SMA(${SMA_PERIOD}) colored by slope direction, shaded band = ${TREND_SLOPE_WINDOW}-bar slope measurement window.`;
    case "rs":
      return `rs — normalized comparison vs ${detail.benchmark || "benchmark"} over last ${RS_WINDOW} bars.`;
    case "ret":
      return row
        ? `ret ${percent(row.expected_return_pct, 1)} — green corridor = current price → resistance.`
        : "ret — green corridor = current price → resistance.";
    case "pos":
      return `pos ${percent(detail.current_position)} — threshold zones within support/resistance; trigger line marks the PosGood floor.`;
    case "rr":
      return row && row.reward_risk != null
        ? `R:R ${row.reward_risk.toFixed(1)}x — green = reward (current → resistance), red = risk (stop → current).`
        : "R:R — green = reward, red = risk.";
  }
}
